import React, { useEffect } from 'react';
import { isPrerender } from '../utils/isPrerender';

/**
 * Google Translate, mounted invisibly. CustomLanguageSelector and
 * useLanguageMode drive the <select> that this widget injects into
 * #google_translate_element, so that element must keep its id.
 *
 * WHY THE DEFENSIVE GUARDS BELOW: this component used to take down the whole
 * application.
 *
 * Google publishes `google.translate` as a namespace EARLY and attaches
 * `TranslateElement` to it LATER. The old code checked the namespace for
 * truthiness and then immediately called `new google.translate.TranslateElement`,
 * so there was a window where the check passed while the constructor was still
 * undefined. `new undefined()` throws a TypeError, and because the throw happens
 * synchronously inside useEffect, React unmounts the ENTIRE tree and the root
 * ErrorBoundary replaces the page with "Something went wrong".
 *
 * It only bit on RE-MOUNT (client-side navigation, back/forward, StrictMode's
 * double invoke), because that is when a script tag already exists and the old
 * code called init immediately instead of waiting for it. A cold load took the
 * callback path and worked. That is why reloading always "fixed" it, and why it
 * looked random to everyone reporting it. Measured before this fix: 0 crashes
 * in 10 cold homepage loads, but 2 in 8 loads of a route that redirects
 * client-side.
 *
 * Three rules here, all load-bearing:
 *   1. Check the CONSTRUCTOR, never the namespace.
 *   2. When the script tag is already present, POLL until the constructor
 *      exists. "Tag is in the DOM" does not mean "script has executed".
 *   3. Wrap construction in try/catch. A third-party widget must never be able
 *      to unmount this application. Losing translation is acceptable; losing
 *      the page is not.
 */

const CONSTRUCTOR_READY = (): boolean =>
  typeof (window as any).google?.translate?.TranslateElement === 'function';

const POLL_MS = 250;
const MAX_TRIES = 40; // ~10s, then give up quietly

const GoogleTranslateWidget = () => {
  useEffect(() => {
    // Never inject during react-snap: the tag gets BAKED into every
    // prerendered page, and on real loads that baked copy started the
    // translate chain (element.js -> translate_http.js -> a render-
    // blocking gstatic stylesheet) BEFORE first paint, part of the
    // blank-screen bug fixed 2026-10-10. Real browsers reach this
    // effect after React boots, which is after first contentful paint.
    if (isPrerender()) return;
    let cancelled = false;
    let pollId: number | undefined;

    /** Returns true when there is nothing further to wait for. */
    const build = (): boolean => {
      const host = document.getElementById('google_translate_element');
      if (!host) return false;
      // Already built (re-mount, or the callback beat the poll). Do not stack
      // a second widget into the same host.
      if (host.childElementCount > 0) return true;
      if (!CONSTRUCTOR_READY()) return false;

      try {
        new (window as any).google.translate.TranslateElement(
          { pageLanguage: 'en', autoDisplay: false },
          'google_translate_element',
        );
      } catch {
        // Google's own code failed. Stop retrying; the page carries on
        // untranslated rather than dying.
      }
      return true;
    };

    // Google invokes this by name, via the ?cb= parameter on the script URL.
    (window as any).googleTranslateElementInit = () => {
      if (!cancelled) build();
    };

    const existingScript = document.querySelector(
      'script[src*="translate.google.com/translate_a/element.js"]',
    );

    if (!existingScript) {
      const script = document.createElement('script');
      script.src =
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      // A blocked or offline CDN must not surface as an application error.
      script.onerror = () => {};
      document.body.appendChild(script);
      return () => {
        cancelled = true;
      };
    }

    // THE CRASH PATH. The tag exists, but it may still be loading or executing,
    // so the constructor may not be attached yet. Poll instead of assuming.
    if (!build()) {
      let tries = 0;
      pollId = window.setInterval(() => {
        tries += 1;
        if (cancelled || build() || tries >= MAX_TRIES) {
          if (pollId !== undefined) window.clearInterval(pollId);
        }
      }, POLL_MS);
    }

    return () => {
      cancelled = true;
      if (pollId !== undefined) window.clearInterval(pollId);
    };
  }, []);

  // The invisible host the widget renders into.
  return <div id="google_translate_element" style={{ display: 'none' }}></div>;
};

export default GoogleTranslateWidget;
